import { useContext } from "react";

import { ProvenanceContext } from "../../context/ProvenanceContext";
import { DagViewerSurface, type DagViewerNode } from "../dag-viewer";

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function operationRunId(value: unknown, requireTyped = false): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const prefix = "operation_run:";
  if (trimmed.startsWith(prefix)) {
    const rawId = trimmed.slice(prefix.length).trim();
    return UUID_LIKE.test(rawId) ? rawId : null;
  }
  return !requireTyped && UUID_LIKE.test(trimmed) ? trimmed : null;
}

function canonicalOperationRunActivation(id: string, node?: DagViewerNode): string | null {
  if (!node || node.metadata.canonical_entity_kind !== "operation_run") return null;
  const nodeId = operationRunId(node.id, true);
  const canonicalId = operationRunId(node.metadata.canonical_id);
  const activationId = operationRunId(id);
  return nodeId && nodeId === canonicalId && nodeId === activationId ? nodeId : null;
}

interface ThreadV5DagExplorerCardProps {
  projectId: string;
  branch: string;
  /** Shared selection seeds every lineage mount. */
  selectedNodeId?: string | null;
  /** Deliberate open-row action: double-click or “Open record”. */
  onSelect?: (id: string) => void;
  /** Pure selection mirror; defaults to ProvenanceContext's URL-backed selection. */
  onSelectInDag?: (id: string | null) => void;
  persistedProject?: boolean;
  compact?: boolean;
  onExpand?: () => void;
}

/**
 * The canonical V5 lineage adapter. Every tile, split pane, focus layer, and
 * full-page graph flows through this wrapper, so they share one renderer and
 * one interaction contract.
 */
export function ThreadV5DagExplorerCard({
  projectId,
  branch,
  selectedNodeId = null,
  onSelect,
  onSelectInDag,
  persistedProject = true,
  compact = false,
  onExpand,
}: ThreadV5DagExplorerCardProps) {
  const provenance = useContext(ProvenanceContext);
  const mirrorSelection = onSelectInDag ?? provenance?.selectArtifact;
  const activateRecord = (id: string, node?: DagViewerNode) => {
    const runId = canonicalOperationRunActivation(id, node);
    if (runId) {
      onSelect?.(runId);
      return;
    }
    // Missing-node and non-run activations remain typed graph selection; they
    // must never be reinterpreted as legacy run identifiers.
    mirrorSelection?.(node?.id ?? id);
  };

  return (
    <div className={`v5-dag-explorer-host${compact ? " is-compact" : ""}`} data-tour="v5-dag-card">
      {persistedProject ? (
        <DagViewerSurface
          projectId={projectId}
          projectName={compact ? "Lineage" : "Project lineage"}
          branch={branch}
          selectedNodeId={selectedNodeId}
          onSelectNode={mirrorSelection}
          onActivateNode={activateRecord}
          variant={compact ? "compact" : "embedded"}
          onExpand={onExpand}
        />
      ) : (
        <div className="v5-dag-explorer-empty">Lineage is available after this project is saved.</div>
      )}
    </div>
  );
}
